import api, { silentApi } from '@/api';
import type {
    DropdownDataWrapper
} from '@/types/api';
import { ApiFactory } from './base/ApiFactory';

/**
 * Service class for interacting with the Dropdown API endpoints.
 * Handles fetching dropdown data for various application features.
 * Uses silentApi to skip global loading indicators.
 */
class DropdownService extends ApiFactory<any> {
    /**
     * Creates an instance of DropdownService.
     */
    constructor() {
        super('/dropdowns', silentApi);
    }

    /**
     * Retrieves a list of branches.
     * @param params - Optional parameters for filtering branches
     * @returns Promise with branches data
     */
    async getBranches(params?: {
        search?: string;
        latitude?: number;
        longitude?: number;
    }): Promise<DropdownDataWrapper<any[]>> {
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/branches`, params);
    }

    /**
     * Retrieves a list of brands.
     * @returns Promise with brands data
     */
    async getBrands(): Promise<DropdownDataWrapper<any[]>> {
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/brands`);
    }

    /**
     * Retrieves downpayment percentage options.
     * @returns Promise with downpayment percentage data
     */
    async getDownpaymentPercentage(): Promise<DropdownDataWrapper<any[]>> {
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/downpayment-percentage`);
    }

    /**
     * Retrieves available loan terms.
     * @param type - Optional product type to filter loan terms
     * @returns Promise with loan terms data
     */
    async getLoanTerms(type?: string): Promise<DropdownDataWrapper<any[]>> {
        const params: any = {};
        if (type) {
            params.product_type = type;
        }
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/loan-terms`, params);
    }

    /**
     * Retrieves civil status options.
     * @param search - Optional search term to filter civil status options
     * @returns Promise with civil status data
     */
    async getCivilStatus(search?: string): Promise<DropdownDataWrapper<any[]>> {
        const params = search ? { search } : undefined;
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/civil-status`, params);
    }

    /**
     * Retrieves a list of countries.
     * @param search - Optional search term to filter countries
     * @returns Promise with countries data
     */
    async getCountries(search?: string): Promise<DropdownDataWrapper<any[]>> {
        const params = search ? { search } : undefined;
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/countries`, params);
    }

    /**
     * Retrieves a list of provinces.
     * @param search - Optional search term to filter provinces
     * @returns Promise with provinces data
     */
    async getProvinces(search?: string): Promise<DropdownDataWrapper<any[]>> {
        const params: any = {};
        if (search) {
            params.search = search;
        }
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/provinces`, params);
    }

    /**
     * Retrieves a list of cities for a given province.
     * @param provinceId - The ID of the province
     * @param search - Optional search term to filter cities
     * @returns Promise with cities data
     */
    async getCities(provinceId: number | string | null, search?: string): Promise<DropdownDataWrapper<any[]>> {
        const params: any = {};
        if (provinceId) {
            params.province_id = provinceId;
        }
        if (search) {
            params.search = search;
        }
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/cities`, params);
    }

    /**
     * Retrieves a list of barangays for a given city.
     * @param cityId - The ID of the city
     * @param search - Optional search term to filter barangays
     * @returns Promise with barangays data
     */
    async getBarangays(cityId: number | string | null, search?: string): Promise<DropdownDataWrapper<any[]>> {
        const params: any = {};
        if (cityId) {
            params.city_id = cityId;
        }
        if (search) {
            params.search = search;
        }
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/barangays`, params);
    }

    /**
     * Retrieves ownership options.
     * @returns Promise with ownership data
     */
    async getOwnership(): Promise<DropdownDataWrapper<any[]>> {
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/ownership`);
    }

    /**
     * Retrieves source of income options.
     * @returns Promise with source of income data
     */
    async getSourceOfIncome(): Promise<DropdownDataWrapper<any[]>> {
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/source-of-income`);
    }

    /**
     * Retrieves gender options.
     * @returns Promise with genders data
     */
    async getGenders(): Promise<DropdownDataWrapper<any[]>> {
        return this.get<DropdownDataWrapper<any[]>>(`${this.resource}/genders`);
    }
}

export const dropdownService = new DropdownService();
export default DropdownService;
